import React from 'react';
import { Stack, Typography, Button, Autocomplete, TextField, useTheme, CustomTheme, Tooltip, Box } from '@mui/material';
import { ROChipMultiSelect, ROToggleButtons, FormRow, ToggleButtons } from 'shared-components/components/FormFields';
import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';

import { FormStateType } from './Interface';
import {
  GET_SYMPTOM_CATEGORIES_AND_SYMPTOMS,
  GET_TREATMENT_REVIEW_SYMPTOMS as GET_TREATMENT_REVIEW_SELECTED_GRADES,
  GET_TREATMENT_REVIEW,
  UPSERT_TREATMENT_REVIEW_SYMPTOM,
  DELETE_TREATMENT_REVIEW_SYMPTOM,
} from './Queries';

interface ToxicitySymptomsProps {
  formState: FormStateType;
  setFormState: React.Dispatch<React.SetStateAction<FormStateType>>;
  updateTreatmentReviewCallback: (data: FormStateType) => void;
  groupId: string;
  isToxicityCategoriesValid: boolean;
  isInvalid: (fieldName: string, value: any, dependentCondition: boolean) => void;
}

type GradeDescriptions = {
  grade1Description?: string;
  grade2Description?: string;
  grade3Description?: string;
  grade4Description?: string;
  grade5Description?: string;
};

const getGradeOptions = (gradeDescriptions: GradeDescriptions) => {
  return [
    { label: '0', value: '0', disabled: false },
    {
      label: gradeDescriptions.grade1Description ? '1' : '-',
      value: '1',
      disabled: !gradeDescriptions.grade1Description,
    },
    {
      label: gradeDescriptions.grade2Description ? '2' : '-',
      value: '2',
      disabled: !gradeDescriptions.grade2Description,
    },
    {
      label: gradeDescriptions.grade3Description ? '3' : '-',
      value: '3',
      disabled: !gradeDescriptions.grade3Description,
    },
    {
      label: gradeDescriptions.grade4Description ? '4' : '-',
      value: '4',
      disabled: !gradeDescriptions.grade4Description,
    },
    {
      label: gradeDescriptions.grade5Description ? '5' : '-',
      value: '5',
      disabled: !gradeDescriptions.grade5Description,
    },
  ];
};

type Symptom = {
  id: string;
  name: string;
  categoryIds: string[];
  grade1Description?: string;
  grade2Description?: string;
  grade3Description?: string;
  grade4Description?: string;
  grade5Description?: string;
};

type ReviewSymptom = {
  id: string;
  grade: number | null;
  symptom: { id: string; name: string };
  manuallyAdded?: boolean;
  createdAt?: string;
};

type SymptomRowType = {
  id: string;
  label: string;
  grade: number | null;
  allowRemoval: boolean;
  gradeDescriptions: GradeDescriptions;
};

const GradeTooltipContent = ({ gradeDescriptions }: { gradeDescriptions: GradeDescriptions }): JSX.Element => {
  return (
    <Stack direction="column" spacing={0.5}>
      <Typography variant="body2" color="inherit">
        <strong>Grade 0:</strong> None
      </Typography>
      {gradeDescriptions.grade1Description && (
        <Typography variant="body2" color="inherit">
          <strong>Grade 1:</strong> {gradeDescriptions.grade1Description}
        </Typography>
      )}
      {gradeDescriptions.grade2Description && (
        <Typography variant="body2" color="inherit">
          <strong>Grade 2:</strong> {gradeDescriptions.grade2Description}
        </Typography>
      )}
      {gradeDescriptions.grade3Description && (
        <Typography variant="body2" color="inherit">
          <strong>Grade 3:</strong> {gradeDescriptions.grade3Description}
        </Typography>
      )}
      {gradeDescriptions.grade4Description && (
        <Typography variant="body2" color="inherit">
          <strong>Grade 4:</strong> {gradeDescriptions.grade4Description}
        </Typography>
      )}
      {gradeDescriptions.grade5Description && (
        <Typography variant="body2" color="inherit">
          <strong>Grade 5:</strong> {gradeDescriptions.grade5Description}
        </Typography>
      )}
    </Stack>
  );
};

const SymptomRow = ({
  id,
  label,
  value,
  handleChange,
  gradeDescriptions,
}: {
  id: string;
  label: React.ReactNode;
  value: string;
  handleChange: (value: string) => void;
  gradeDescriptions: GradeDescriptions;
}): JSX.Element => {
  const theme: CustomTheme = useTheme<CustomTheme>();
  const gradeOptions = getGradeOptions(gradeDescriptions);

  return (
    <FormRow fieldlabel={label} id={`toxicity-symptoms-row-${id}`}>
      <Tooltip
        placement="right"
        arrow
        slotProps={{
          tooltip: {
            sx: {
              backgroundColor: theme.palette.primary.light,
              maxWidth: 400,
            },
          },
          arrow: {
            sx: {
              color: theme.palette.primary.light,
            },
          },
        }}
        title={<GradeTooltipContent gradeDescriptions={gradeDescriptions} />}>
        <Box sx={{ width: '100%' }}>
          <ToggleButtons
            id={`toxicity-symptoms-toggle-${id}`}
            options={gradeOptions}
            handleChange={handleChange}
            value={value}
            fullWidth
            sx={{ width: '100%', maxWidth: '100%', marginLeft: '-24px', minWidth: 'calc(100% + 24px)' }}
          />
        </Box>
      </Tooltip>
    </FormRow>
  );
};

const DEFAULT_GRADE_OPTIONS = [
  { label: '0', value: '0' },
  { label: '1', value: '1' },
  { label: '2', value: '2' },
  { label: '3', value: '3' },
  { label: '4', value: '4' },
  { label: '5', value: '5' },
];

const AddSymptomRow = ({
  options,
  handleChange,
}: {
  options: { label: string; value: string }[];
  handleChange: (value: string) => void;
}): JSX.Element => {
  return (
    <ROToggleButtons
      id="toxicity-symptoms-row-add"
      fieldlabel={
        <Stack direction="row" gap={1} alignItems="center">
          <Autocomplete
            id="other-symptom"
            size="small"
            fullWidth
            disablePortal
            sx={{ width: '250px', paddingLeft: 2, marginTop: '-8px' }}
            options={options}
            renderInput={(params) => <TextField {...params} size="small" sx={{ width: '100%' }} />}
            onChange={(event, value) => {
              handleChange(value ? (value as { value: string }).value : '');
            }}
          />
        </Stack>
      }
      options={DEFAULT_GRADE_OPTIONS}
      handleChange={() => {}}
      fullWidth
      sx={{ marginLeft: '-24px', minWidth: 'calc(100% + 24px)' }}
    />
  );
};

const ToxicitySymptoms = (props: ToxicitySymptomsProps): JSX.Element => {
  const theme: CustomTheme = useTheme<CustomTheme>();
  const { formState, setFormState, groupId, isToxicityCategoriesValid, isInvalid } = props;

  // Init data and mutations
  const { data: symptomData, loading: symptomDataLoading } = useQuery(GET_SYMPTOM_CATEGORIES_AND_SYMPTOMS, {
    fetchPolicy: 'cache-and-network',
    variables: { active: true },
  });
  const {
    data: selectedGradeData,
    loading: selectedGradeLoading,
    refetch: refetchReviewSymptoms,
  } = useQuery(GET_TREATMENT_REVIEW_SELECTED_GRADES, {
    variables: { groupId },
    fetchPolicy: 'cache-first',
  });
  const [upsertReviewSymptom] = useMutation(UPSERT_TREATMENT_REVIEW_SYMPTOM, {
    refetchQueries: [
      { query: GET_TREATMENT_REVIEW_SELECTED_GRADES, variables: { groupId } },
      { query: GET_TREATMENT_REVIEW, variables: { groupId } },
    ],
  });
  const [deleteReviewSymptom] = useMutation(DELETE_TREATMENT_REVIEW_SYMPTOM, {
    refetchQueries: [
      { query: GET_TREATMENT_REVIEW_SELECTED_GRADES, variables: { groupId } },
      { query: GET_TREATMENT_REVIEW, variables: { groupId } },
    ],
  });

  // Extract values from query data
  const categories = symptomData?.toxicityCategories || [];
  const allSymptoms: Symptom[] = symptomData?.toxicitySymptoms || [];
  const selectedGrades: ReviewSymptom[] = selectedGradeData?.treatmentReviewSymptoms || [];
  const selectedCategories = formState.toxicityCategories || [];

  const categoriesById = React.useMemo<Map<string, string>>(
    () => new Map<string, string>((categories as Array<{ id: string; name: string }>).map((c) => [c.id, c.name])),
    [categories],
  );

  const displayedSymptoms = React.useMemo<SymptomRowType[]>(() => {
    // Determines the list of symptoms displayed, based on whether it belongs to a selected category
    // or has been added manually
    // Category symptoms appear first (in order of allSymptoms), then manually added symptoms at the bottom
    // sorted by creation time

    const categorySymptoms: SymptomRowType[] = [];
    const manualSymptoms: (SymptomRowType & { createdAt?: string })[] = [];

    allSymptoms.forEach((symptom) => {
      // Get grade number if it exists
      const gradeSelection = selectedGrades.find((selectedGrade) => selectedGrade.symptom.id === symptom.id);
      const manuallyAdded = gradeSelection?.manuallyAdded || false;

      // Check if there is an existing selection for this symptom
      const inSelectedCategory = symptom.categoryIds?.some((id) => selectedCategories.includes(id));

      const gradeDescriptions: GradeDescriptions = {
        grade1Description: symptom.grade1Description,
        grade2Description: symptom.grade2Description,
        grade3Description: symptom.grade3Description,
        grade4Description: symptom.grade4Description,
        grade5Description: symptom.grade5Description,
      };

      // Determine if it should be displayed
      if (inSelectedCategory) {
        // Category-based symptoms go first
        categorySymptoms.push({
          id: symptom.id,
          label: symptom.name,
          grade: gradeSelection?.grade ?? null,
          allowRemoval: false,
          gradeDescriptions,
        });
      } else if (manuallyAdded) {
        // Manually added symptoms go at the bottom
        manualSymptoms.push({
          id: symptom.id,
          label: symptom.name,
          grade: gradeSelection?.grade ?? null,
          allowRemoval: true,
          createdAt: gradeSelection?.createdAt,
          gradeDescriptions,
        });
      }
    });

    // Sort manually added symptoms by creation time (oldest first)
    manualSymptoms.sort((a, b) => {
      if (!a.createdAt || !b.createdAt) return 0;
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });

    return [...categorySymptoms, ...manualSymptoms];
  }, [allSymptoms, selectedGrades, selectedCategories]);

  const availableSymptoms = React.useMemo(() => {
    const displayedIds = new Set(displayedSymptoms.map((r) => r.id));
    return allSymptoms.filter((s) => !displayedIds.has(s.id)).map((s) => ({ label: s.name, value: s.id }));
  }, [allSymptoms, displayedSymptoms]);

  const [showAddRow, setShowAddRow] = React.useState(false);

  // Wait for data to load before rendering
  if ((symptomDataLoading && !symptomData) || selectedGradeLoading) {
    return <></>;
  }

  return (
    <Stack
      direction="column"
      sx={{
        borderLeft: '1px solid',
        borderColor: 'divider',
        paddingLeft: '24px',
        '& .MuiAutocomplete-root': { marginLeft: '-24px', minWidth: 'calc(100% + 24px)' },
      }}>
      <ROChipMultiSelect
        id="toxicity-category"
        fieldLabel="Select Category"
        selectedValues={
          formState.toxicityCategories
            ?.map((id) => categories.find((c: { id: string }) => c.id === id)?.name)
            .filter((name): name is string => name !== undefined) || []
        }
        options={categories.map((option: any) => option.name) || []}
        handleChange={(values: string[]): void => {
          const newdata: FormStateType = {
            ...formState,
            toxicityCategories: values
              .map((name) => categories.find((c: { name: string }) => c.name === name)?.id)
              .filter((id): id is string => id !== undefined),
          };
          setFormState(newdata);
          props.updateTreatmentReviewCallback(newdata);
          isInvalid('toxicityCategories', newdata.toxicityCategories, newdata.toxicityType === 'CTCAE v5');
        }}
        fullWidth
        required
        requiredFieldColor={theme.palette.error.main}
        error={isToxicityCategoriesValid}
      />
      <Typography variant="subtitle1" color="text.secondary">
        {formState.toxicityCategories?.length > 0
          ? formState.toxicityCategories
              .map((id) => categoriesById.get(id))
              .filter(Boolean)
              .join(', ')
          : ''}
      </Typography>
      {displayedSymptoms.map(({ id, label, grade, allowRemoval: manuallyAdded, gradeDescriptions }) => {
        const fieldLabel = manuallyAdded ? (
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="body1">{label}</Typography>
            <IconButton
              aria-label="remove manual symptom"
              size="small"
              onClick={() => {
                deleteReviewSymptom({
                  variables: { groupId, symptomId: id },
                }).then(() => {
                  refetchReviewSymptoms();
                });
              }}>
              <RemoveCircleOutline color="secondary" fontSize="small" />
            </IconButton>
          </Stack>
        ) : (
          label
        );
        return (
          <SymptomRow
            key={id}
            id={id}
            label={fieldLabel}
            value={grade != null ? String(grade) : ''}
            handleChange={(val) => {
              const chosenGrade = val === '' ? null : Number(val);
              upsertReviewSymptom({
                variables: {
                  groupId,
                  symptomId: id,
                  grade: chosenGrade,
                },
              }).then(() => {
                refetchReviewSymptoms();
              });
            }}
            gradeDescriptions={gradeDescriptions}
          />
        );
      })}
      {showAddRow && (
        <AddSymptomRow
          options={availableSymptoms}
          handleChange={(symptomId: string) => {
            if (!symptomId) {
              setShowAddRow(false);
              return;
            }
            upsertReviewSymptom({
              variables: {
                groupId,
                symptomId,
                grade: null,
              },
            }).then(() => {
              setShowAddRow(false);
              refetchReviewSymptoms();
            });
          }}
        />
      )}
      <Button
        variant="text"
        startIcon={<AddCircleOutline color="primary" />}
        sx={{ alignSelf: 'flex-start' }}
        disabled={availableSymptoms.length === 0}
        onClick={() => {
          setShowAddRow(true);
        }}>
        ADD A SYMPTOM
      </Button>
    </Stack>
  );
};

export default ToxicitySymptoms;
